import { HStack } from "@chakra-ui/layout";
import { Box, Button, Stack, Text, VStack } from "@chakra-ui/react";
import { FormikProps, withFormik } from "formik";
import React, { useEffect, useState } from "react";
import { CherryPayApi } from "../../api/models";
import { Card } from "../../components/Card/Card";
import { NotificationPreview } from "../../components/NotificationPreview/NotificationPreview";
import { NotificationPreviewType } from "../../components/ImagePreview/ImagePreview";
import { StartNotificationScheduleIcon } from "../../styles/icons";
import DateUtil from "../../util/DateUtil";
import { NotificationFormProps, NotificationFormValues } from "./types";
import { handleNotificationSubmit } from "./SubmitHandler";
import { validateNotificationForm } from "./ValidateHandler";
import { mapNotificationPropsToValues } from "./MapPropsToValuesHandler";
import { PushNotificationSettings } from "./PushNotificationSettings";
import { EligibleMembers } from "./EligibleMembers";
import { NotificationSchedule } from "./NotificationSchedule";
import { getMaxSendDateTime } from "./NotificationUtils";
import { useToast } from "../../hooks/useToast";
import { useConfirmationDialog } from "../../hooks/useConfirmationDialog";
import { useNotificationRiskRating } from "../../hooks/useNotificationRiskRating";
import { validateNotificationSchedule } from "./NotificationScheduleHandler";
import { isEmptyStr } from "../../util/StringUtil";
import { useStickyOverflowFix } from "../../hooks/useStickyOverflowFix";
import { useGeoLocations } from "../../hooks/useGeoLocations";

const SILENT_PASS_UPDATE_DIALOG_TITLE = "Silent pass update";
const SILENT_PASS_UPDATE_DIALOG_MESSAGE =
  "Are you sure you want to create a silent pass update?";

type SectionKey = "push" | "geo" | "members" | "schedule";

// Store timezones at module level so they can be accessed by validate function
let moduleTimezones: any[] = [];

export const setTimezones = (
  timezones: CherryPayApi.EnumConfigurationItem[]
) => {
  moduleTimezones = timezones || [];
};

const InnerForm = ({
  isSubmitting,
  isValid,
  values,
  businessId,
  allowSilentEmptyPassUpdate,
  notification,
  notificationName,
  onCancel,
  submitForm,
  setFieldValue,
  apiClient,
  submitCount,
  errors,
}: NotificationFormProps & FormikProps<NotificationFormValues>) => {
  const [memberCount, setMemberCount] = useState<number | null>(null);
  const [selectedGeoLocationId, setSelectedGeoLocationId] = useState<
    string | null
  >(null);
  const [notificationType, setNotificationType] =
    useState<NotificationPreviewType>("push");
  const [openSection, setOpenSection] = useState<SectionKey | null>("push");

  const {
    locations: geoLocations,
    isLoading: isLoadingGeoLocations,
    hasFetched: hasFetchedGeoLocations,
  } = useGeoLocations(businessId);

  const handleSectionToggle = (section: SectionKey) => () => {
    setOpenSection(openSection === section ? null : section);
  };

  useEffect(() => {
    if (geoLocations.length > 0 && !selectedGeoLocationId) {
      setSelectedGeoLocationId(geoLocations[0].GeoLocationId);
    }
  }, [geoLocations, selectedGeoLocationId]);

  const { showErrorToast } = useToast();
  const { confirmationElement, showConfirmationDialog } =
    useConfirmationDialog();

  // Get risk rating for the notification message
  const { riskLevel, riskRatingReason, suggestedMessage, isReviewingMessage } =
    useNotificationRiskRating({
      businessId,
      message: values.passUpdateMessage,
    });

  // Override body overflow-x to allow sticky positioning in this form
  useStickyOverflowFix();

  // Show error toast when form is submitted with validation errors
  useEffect(() => {
    if (
      submitCount > 0 &&
      !isValid &&
      errors &&
      Object.keys(errors).length > 0
    ) {
      showErrorToast("Please check all fields and fix any errors");
    }
  }, [submitCount, isValid, errors, showErrorToast]);

  // Fetch member count when filter query changes
  useEffect(() => {
    if (businessId) {
      setMemberCount(null);
      apiClient
        .getNotificationScheduleEligibleMemberCount(
          businessId,
          values.memberFilterQuery
        )
        .then((result) => {
          if (result.ok) {
            setMemberCount(result.data.Count ?? 0);
          } else {
            setMemberCount(null);
            console.error(
              "Failed to fetch notification member count:",
              result.message
            );
          }
        });
    }
  }, [apiClient, businessId, values.memberFilterQuery]);
  // Create a preview notification object for the preview component
  const localDateTime = DateUtil.getLocalDateTimeInUTCFormatFromDateObject(
    values.sendDateTime
  );
  const previewNotification: CherryPayApi.CherryPassNotificationScheduleDetail =
    {
      DisplayName: values.displayName || "Notification Name",
      PassUpdateMessage: values.passUpdateMessage,
      TimeZoneId: values.timeZoneId || "UTC",
      LocalScheduleSendDateTime: localDateTime,
      MaximumLocalScheduleSendDateTime: localDateTime
        ? getMaxSendDateTime(new Date(localDateTime), values)
        : null,
      MemberFilterQuery: values.memberFilterQuery,
      NotificationScheduleStatus: "Draft",
      AutomaticallyScheduleForNewPassesGenerated: true,
    };

  const isEditMode = notification !== null;
  const isDraftStatus =
    (notification?.NotificationScheduleStatus ?? "Draft") === "Draft";
  const saveButtonLabel = isDraftStatus ? (
    <>{isEditMode ? "Update Draft" : "Save Draft"}</>
  ) : (
    <>{isEditMode ? "Update" : "Save"}</>
  );

  const handleScheduleClick = async () => {
    if (allowSilentEmptyPassUpdate && isEmptyStr(values.passUpdateMessage)) {
      const silentConfirm = await showConfirmationDialog(
        SILENT_PASS_UPDATE_DIALOG_TITLE,
        SILENT_PASS_UPDATE_DIALOG_MESSAGE,
        "Continue"
      );
      if (!silentConfirm.ok) {
        return;
      }
    }

    // Validate the notification can be scheduled based on risk rating
    const canSchedule = await validateNotificationSchedule({
      riskLevel,
      isReviewingMessage,
      message: values.passUpdateMessage,
      showErrorToast,
      showConfirmationDialog,
    });

    if (!canSchedule) {
      return;
    }

    // Proceed with scheduling
    setFieldValue("isScheduleRequested", true);
    submitForm();
  };

  const handleSaveClick = async () => {
    if (allowSilentEmptyPassUpdate && isEmptyStr(values.passUpdateMessage)) {
      const silentConfirm = await showConfirmationDialog(
        SILENT_PASS_UPDATE_DIALOG_TITLE,
        SILENT_PASS_UPDATE_DIALOG_MESSAGE,
        "Continue"
      );
      if (!silentConfirm.ok) {
        return;
      }
    }

    setFieldValue("isScheduleRequested", false);
    submitForm();
  };

  return (
    <Card w="100%" p={0}>
      <Stack
        w="100%"
        spacing={{ base: 4, xl: 24 }}
        py={3}
        px={8}
        borderTopStartRadius="3xl"
        borderTopEndRadius="3xl"
        direction={{ base: "column", xl: "row" }}
        borderBottomWidth={"2px"}
        borderBottomStyle={"solid"}
        borderBottomColor={"cherryBackground.placeholder"}
      >
        <VStack spacing="1" alignItems="flex-start">
          <Text fontSize="2xl" fontWeight="bold">
            {notification != null ? notificationName : "New Push Notification"}
          </Text>
        </VStack>

        <HStack
          flex="1"
          justifyContent="end"
          position="relative"
          zIndex={100}
          alignSelf="center"
        >
          <Box>
            <Button
              disabled={isSubmitting}
              onClick={onCancel}
              backgroundColor={"white"}
              color={"black"}
              borderColor={"cancelButton.border"}
              borderWidth={"2px"}
            >
              Cancel
            </Button>
          </Box>

          <Box>
            <Button
              backgroundColor={"white"}
              borderColor={"cherryButton.800"}
              color={"cherryButton.800"}
              borderWidth={"2px"}
              _hover={{
                backgroundColor: "cherryButton.25",
              }}
              _active={{
                backgroundColor: "cherryButton.25",
              }}
              type="submit"
              isLoading={isSubmitting}
              disabled={isSubmitting || !isValid}
              onClick={handleSaveClick}
            >
              {saveButtonLabel}
            </Button>
          </Box>

          {isDraftStatus && (
            <Box>
              <Button
                colorScheme={"cherryButton"}
                type="button"
                isLoading={isSubmitting}
                disabled={isSubmitting || !isValid}
                onClick={handleScheduleClick}
                leftIcon={<StartNotificationScheduleIcon color="white" />}
              >
                Schedule
              </Button>
            </Box>
          )}
        </HStack>
      </Stack>

      <VStack
        w="100%"
        p={0}
        color="cherryGrey.900"
        bgColor="white"
        borderBottomLeftRadius="3xl"
        borderBottomRightRadius="3xl"
        boxShadow="md"
        spacing={6}
        align="start"
      >
        <Stack w="100%" direction={["column", "row"]} spacing={0} align="start">
          {/* Left side - Form */}
          <Box w={["100%", "50%"]} p={4}>
            <PushNotificationSettings
              riskLevel={riskLevel}
              riskRatingReason={riskRatingReason}
              suggestedMessage={suggestedMessage}
              isReviewingMessage={isReviewingMessage}
              geoLocations={geoLocations}
              isLoadingGeoLocations={isLoadingGeoLocations}
              hasFetchedGeoLocations={hasFetchedGeoLocations}
              selectedGeoLocationId={selectedGeoLocationId}
              onSelectGeoLocation={setSelectedGeoLocationId}
              onLocationMessageSaved={() => setNotificationType("geo")}
              isPushSettingsOpen={openSection === "push"}
              onTogglePushSettings={handleSectionToggle("push")}
              isGeoLocationsOpen={openSection === "geo"}
              onToggleGeoLocations={handleSectionToggle("geo")}
            />
            <EligibleMembers
              businessId={businessId}
              notificationScheduleId={notification?.Meta?.Id ?? null}
              isOpen={openSection === "members"}
              onToggle={handleSectionToggle("members")}
            />
            <NotificationSchedule
              businessId={businessId}
              isOpen={openSection === "schedule"}
              onToggle={handleSectionToggle("schedule")}
            />
          </Box>

          {/* Right side - Preview */}
          <Box
            w={["100%", "50%"]}
            backgroundColor={"cherryBackground.placeholder"}
            position="sticky"
            top={"20px"}
            alignSelf="flex-start"
          >
            <NotificationPreview
              notification={previewNotification}
              memberCount={memberCount}
              geoLocations={geoLocations}
              selectedGeoLocationId={selectedGeoLocationId}
              isGeoSectionOpen={openSection === "geo"}
              notificationType={notificationType}
              onNotificationTypeChange={setNotificationType}
            />
          </Box>
        </Stack>
      </VStack>
      {confirmationElement}
    </Card>
  );
};

export const NotificationForm = withFormik<
  NotificationFormProps,
  NotificationFormValues
>({
  enableReinitialize: true,
  mapPropsToValues: mapNotificationPropsToValues,

  handleSubmit: async (values, { props, setFieldError }) => {
    await handleNotificationSubmit(values, props, setFieldError);
  },

  validateOnMount: false,
  validateOnBlur: true,

  validate: (values) => validateNotificationForm(values, moduleTimezones),
})(InnerForm);
